import { useEffect, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { TextInput } from "@/components/ui/Field";
import { ApiError } from "@/lib/apiClient";
import {
  convertAnfrageToOrder,
  type AnfrageDetail,
  type AnfragePosition,
  type ConvertAnfrageResult,
} from "@/lib/anfragen";
import { canConvertAnfrage, positionAuftragDisplay } from "@/pages/anfragen/anfrageWizardUtils";
import { AuftragPickerDialog } from "@/pages/bestellungen/AuftragPickerDialog";
import { useOrderWizard } from "@/pages/bestellungen/useOrderWizard";
import type { AuftragMini } from "@/lib/orders";
import { orderTextWithArticleNumber } from "@/lib/orderDraftUtils";

interface PositionDraft {
  position: AnfragePosition;
  selected: boolean;
  menge: string;
  einheit: string;
  text: string;
}

function buildDrafts(detail: AnfrageDetail | null): PositionDraft[] {
  return (detail?.positionen || [])
    .filter((p) => p.id != null)
    .map((p) => ({
      position: p,
      selected: true,
      menge: String(p.menge ?? ""),
      einheit: p.einheit || "",
      text: orderTextWithArticleNumber(p.text || "", p.hero_nr),
    }));
}

export function ConvertToOrderDialog({
  open,
  detail,
  onClose,
  onConverted,
}: {
  open: boolean;
  detail: AnfrageDetail | null;
  onClose: () => void;
  onConverted: (result: ConvertAnfrageResult) => void;
}) {
  const { openDraftById } = useOrderWizard();
  const [drafts, setDrafts] = useState<PositionDraft[]>([]);
  const [targetAuftrag, setTargetAuftrag] = useState<AuftragMini | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDrafts(buildDrafts(detail));
    setTargetAuftrag(null);
    setError(null);
  }, [open, detail]);

  function updateDraft(positionId: number, patch: Partial<PositionDraft>) {
    setDrafts((prev) =>
      prev.map((row) => (row.position.id === positionId ? { ...row, ...patch } : row)),
    );
  }

  async function submit() {
    if (!detail) return;
    if (!canConvertAnfrage(detail)) {
      setError("Entwürfe können nicht in eine Bestellung umgewandelt werden – bitte die Anfrage zuerst abschließen.");
      return;
    }
    const selected = drafts.filter((d) => d.selected);
    if (!selected.length) {
      setError("Bitte mindestens eine Position wählen.");
      return;
    }
    const position_edits: Array<{
      position_id: number;
      menge: number;
      einheit?: string | null;
      text: string;
    }> = [];
    for (const row of selected) {
      const pid = Number(row.position.id);
      const menge = Number(row.menge);
      const text = row.text.trim();
      if (!Number.isFinite(menge) || menge <= 0) {
        setError("Bitte für jede markierte Position eine gültige Menge größer 0 eingeben.");
        return;
      }
      if (!text) {
        setError("Bitte für jede markierte Position einen Text angeben.");
        return;
      }
      position_edits.push({
        position_id: pid,
        menge,
        einheit: row.einheit.trim() || null,
        text,
      });
    }

    setLoading(true);
    setError(null);
    try {
      const result = await convertAnfrageToOrder({
        anfrage_id: detail.id,
        position_ids: position_edits.map((p) => p.position_id),
        target_auftrag_id: targetAuftrag?.id ?? null,
        position_edits,
      });
      const draftId = result.order_draft_id;
      if (draftId) {
        const taNums = [
          ...new Set(
            (detail.positionen || [])
              .map((p) => p.ta_nummer)
              .filter((n): n is string => !!n && String(n).trim() !== ""),
          ),
        ];
        const title = taNums.length
          ? `Bestellung aus ${taNums.join(", ")}`
          : detail.subject?.trim()
            ? `Bestellung aus ${detail.subject.trim()}`
            : "Bestellung aus Anfrage";
        await openDraftById(draftId, title);
        onConverted(result);
        onClose();
      } else {
        setError("Entwurf konnte nicht erstellt werden.");
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Umwandlung fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={loading ? () => {} : onClose}
        title="In Bestellung umwandeln"
        size="lg"
        zIndexClass="z-[120]"
        footer={
          <>
            <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
              Abbrechen
            </Button>
            <Button type="button" onClick={() => void submit()} loading={loading}>
              Umwandeln
            </Button>
          </>
        }
      >
        <p className="mb-3 text-sm text-label-secondary">
          Markieren Sie die Positionen für die Bestellung. <strong>Menge</strong>, <strong>Einheit</strong> und{" "}
          <strong>Text</strong> können Sie hier noch anpassen (nur für die neue TK, die Anfrage in der Datenbank
          bleibt unverändert).
        </p>

        {error ? <p className="mb-3 text-sm text-status-red">{error}</p> : null}

        <div className="mb-4 space-y-2">
          {drafts.map((row) => {
            const pid = Number(row.position.id);
            const disp = positionAuftragDisplay(row.position);
            return (
              <div
                key={pid}
                className="rounded-ios border border-separator bg-card p-3 shadow-ios"
              >
                <label className="mb-2 flex cursor-pointer items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-separator-opaque text-accent focus:ring-accent-soft"
                    checked={row.selected}
                    onChange={(e) => updateDraft(pid, { selected: e.target.checked })}
                  />
                  <span className="text-label-secondary">
                    Übernehmen · Auftrag <span className="text-label">{disp || "–"}</span>
                  </span>
                </label>
                <div className="grid gap-2 sm:grid-cols-12">
                  <div className="sm:col-span-2">
                    <TextInput
                      label="Menge"
                      type="number"
                      min={0}
                      step="any"
                      value={row.menge}
                      onChange={(e) => updateDraft(pid, { menge: e.target.value })}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <TextInput
                      label="Einh."
                      value={row.einheit}
                      placeholder="z. B. Stk."
                      onChange={(e) => updateDraft(pid, { einheit: e.target.value })}
                    />
                  </div>
                  <div className="sm:col-span-8">
                    <TextInput
                      label="Position / Artikeltext"
                      value={row.text}
                      onChange={(e) => updateDraft(pid, { text: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <section className="rounded-ios border border-separator bg-fill-quaternary/15 p-3">
          <p className="mb-1 text-sm font-semibold text-label">
            Ziel-Auftrag <span className="font-normal text-label-secondary">(optional)</span>
          </p>
          <p className="mb-3 text-xs text-label-secondary">
            <strong>Standard:</strong> Es werden die Aufträge aus der Anfrage übernommen – jede Position behält
            „ihre“ Auftragsnummer, ggf. entstehen mehrere Bestellungen.
            <br />
            <strong>Nur wenn nötig:</strong> Wählen Sie hier einen anderen Auftrag, werden{" "}
            <em>alle</em> markierten Positionen diesem einen Auftrag zugeordnet (eine Bestellung mit der{" "}
            <strong>Auftragsnummer des gewählten Auftrags</strong>).
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <span className="min-w-0 flex-1 text-sm text-label">
              {targetAuftrag
                ? targetAuftrag.auftragsnummer_display || targetAuftrag.auftragsnummer || `Auftrag #${targetAuftrag.id}`
                : "Leer lassen = Auftragsnummern aus der Anfrage"}
            </span>
            <Button type="button" variant="secondary" size="sm" onClick={() => setPickerOpen(true)}>
              Auftrag wählen …
            </Button>
            {targetAuftrag ? (
              <Button type="button" variant="ghost" size="sm" onClick={() => setTargetAuftrag(null)}>
                Entfernen
              </Button>
            ) : null}
          </div>
        </section>
      </Dialog>

      <AuftragPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(auftrag) => setTargetAuftrag(auftrag)}
      />
    </>
  );
}
